import { apiFetch } from '../utils/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const runPayroll = async (payload) => {
  const response = await apiFetch(`${API_BASE_URL}/api/payroll/process`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = 'Failed to run payroll';
    try {
      const errorData = await response.json();
      message = errorData.message || errorData.error || message;
    } catch (error) {
      // Keep the default message when the response is not JSON.
    }
    throw new Error(message);
  }

  const disposition = response.headers.get('content-disposition') || '';
  const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : null;
  const fileBlob = await response.blob();

  return {
    fileBlob,
    filename,
    contentType: response.headers.get('content-type') || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };
};

const parseJsonResponse = async (response, fallbackMessage) => {
  if (!response.ok) {
    let message = fallbackMessage;
    try {
      const errorData = await response.json();
      message = errorData.message || errorData.error || message;
    } catch (error) {
      // Keep the default message when the response is not JSON.
    }
    throw new Error(message);
  }

  return response.json();
};

const fetchFirstAvailableJson = async (requests, fallbackMessage) => {
  const notFoundUrls = [];
  let lastErrorMessage = fallbackMessage;

  for (const request of requests) {
    const response = await apiFetch(request.url, request.options);

    if (response.status === 404) {
      notFoundUrls.push(request.url);
      continue;
    }

    try {
      return await parseJsonResponse(response, fallbackMessage);
    } catch (error) {
      lastErrorMessage = error.message || fallbackMessage;
      throw error;
    }
  }

  throw new Error(`${lastErrorMessage}. Backend payroll report endpoint was not found. Tried: ${notFoundUrls.join(', ')}`);
};

export const getPayrollReport = async ({ month, year, page = 0, size = 15, status, search } = {}) => {
  const params = new URLSearchParams({
    month: String(month),
    year: String(year),
    page: String(page),
    size: String(size),
  });
  if (status) params.append('status', status);
  if (search) params.append('search', search);
  const query = params.toString();

  return fetchFirstAvailableJson(
    [
      { url: `${API_BASE_URL}/api/payroll/report?${query}` },
      { url: `${API_BASE_URL}/api/payroll/reports?${query}` },
      { url: `${API_BASE_URL}/api/payroll?${query}` },
      { url: `${API_BASE_URL}/api/payroll/${year}/${month}` },
      { url: `${API_BASE_URL}/api/payroll/report/${year}/${month}` },
    ],
    'Failed to load payroll report'
  );
};

// The calling employee's own payroll row for a month, scoped server-side to their own empId -
// used by the Payslip page instead of fetching the whole client's report and filtering
// client-side. Returns null if nothing has been processed for them yet that month.
export const getMyPayrollRecord = async ({ month, year }) => {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  const response = await apiFetch(`${API_BASE_URL}/api/payroll/my?${params.toString()}`);
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error('Failed to check payroll status');
  }
  return response.json();
};

// Employee ids that already have a processed payroll row for this month/year - used by the
// Run Payroll screen to auto-exclude and lock them from being processed a second time.
export const getProcessedEmployeeIds = async ({ month, year }) => {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  const response = await apiFetch(`${API_BASE_URL}/api/payroll/processed-employees?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Failed to check already-processed employees');
  }
  return response.json();
};

export const updatePayrollStatus = async ({ payrollId, employeeId, month, year, status }) => {
  const options = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      employeeId,
      month,
      year,
      status,
      creditStatus: status,
      paymentStatus: status,
    }),
  };

  const requests = payrollId
    ? [
        { url: `${API_BASE_URL}/api/payroll/${payrollId}/status`, options },
        { url: `${API_BASE_URL}/api/payroll/status`, options },
        { url: `${API_BASE_URL}/api/payroll/${payrollId}/payment-status`, options },
      ]
    : [
        { url: `${API_BASE_URL}/api/payroll/status`, options },
        { url: `${API_BASE_URL}/api/payroll/payment-status`, options },
      ];

  return fetchFirstAvailableJson(requests, 'Failed to update payroll status');
};

export const updatePayslipMode = async ({ payrollId, employeeId, month, year, manualPayslip }) => {
  const response = await apiFetch(`${API_BASE_URL}/api/payroll/${payrollId}/payslip-mode`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ employeeId, month, year, manualPayslip }),
  });

  return parseJsonResponse(response, 'Failed to update payslip mode');
};

export const uploadManualPayslip = async (payrollId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiFetch(`${API_BASE_URL}/api/payroll/${payrollId}/payslip`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    let message = 'Failed to upload payslip';
    try {
      const errorData = await response.json();
      message = errorData.message || errorData.error || message;
    } catch (error) {
      // Keep the default message when the response is not JSON.
    }
    throw new Error(message);
  }
};

export const getManualPayslipUrl = (payrollId) => `${API_BASE_URL}/api/payroll/${payrollId}/payslip`;

// One payroll row with its earnings/deductions breakdown, for the admin's Edit Pay dialog.
export const getPayrollDetail = async (payrollId) => {
  const response = await apiFetch(`${API_BASE_URL}/api/payroll/${payrollId}/detail`);
  return parseJsonResponse(response, 'Failed to load payroll details');
};

// Saves the admin's edits to the pay - net pay and totals are recomputed server-side.
export const updatePayrollBreakdown = async (payrollId, { workingDays, lop, variablePay, earnings, deductions }) => {
  const response = await apiFetch(`${API_BASE_URL}/api/payroll/${payrollId}/breakdown`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workingDays, lop, variablePay, earnings, deductions }),
  });
  return parseJsonResponse(response, 'Failed to save payroll changes');
};

// Discards manual edits and recomputes the pay from attendance.
export const recalculatePayroll = async (payrollId) => {
  const response = await apiFetch(`${API_BASE_URL}/api/payroll/${payrollId}/recalculate`, { method: 'POST' });
  return parseJsonResponse(response, 'Failed to recalculate payroll');
};

// The month's full payroll as an .xlsx - every active employee, coloured by status (credited /
// processed / not processed), with the amount and date credited.
export const downloadPayrollExcel = async ({ month, year }) => {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  const response = await apiFetch(`${API_BASE_URL}/api/payroll/process?${params.toString()}`, {
    headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  });
  if (!response.ok) {
    let message = 'Failed to download payroll Excel';
    try {
      const errorData = await response.json();
      message = errorData.message || errorData.error || message;
    } catch (error) {
      // Keep the default message when the response is not JSON.
    }
    throw new Error(message);
  }
  return response.blob();
};
